import { useSearchParamsContext } from '@internal/blocks/SearchResultsBlock/SearchParamsContext';
import { KnownBaleChamberType } from '@internal/utils/machine/KnownBalerChamberType';
import { KnownMachineHeaderType } from '@internal/utils/machine/KnownMachineHeaderType';
import { MachineType } from '@internal/utils/machine/MachineType';
import { TFilterableBaleChamberOption } from '@internal/utils/search/TFilterableBalerChamberOption';
import { TFilterableHeaderTypeOption } from '@internal/utils/search/TFilterableHeaderTypeOption';
import { TFilterOption } from '@renderer-ui-library/molecules';
import { FormControlLabel, Radio, RadioGroup } from '@renderer-ui-library/mui';
import React, { useCallback, useEffect, useState } from 'react';
import { useBaleChamberTypeOptions } from './useRadioSelectOptions';

type Props = {
    onChange: (
        key: KnownBaleChamberType | KnownMachineHeaderType | null,
    ) => void;
    value:
        | TFilterableBaleChamberOption
        | TFilterableHeaderTypeOption
        | undefined;
    canToggle?: boolean;
};

export const RadioSelectFilter: React.FC<Props> = React.memo(
    ({ onChange, value, canToggle }) => {
        const { baleChamberTypeOptions, machineHeaderTypeOptions } =
            useBaleChamberTypeOptions();
        const {
            searchParams: { machineType },
        } = useSearchParamsContext();

        const [options, setOptions] = useState<TFilterOption[]>([]);

        useEffect(() => {
            if (machineType === MachineType.BALER) {
                setOptions(baleChamberTypeOptions);
            }
            if (machineType === MachineType.HEADER) {
                setOptions(machineHeaderTypeOptions);
            }
        }, [baleChamberTypeOptions, machineHeaderTypeOptions, machineType]);

        const handleRadioClick = useCallback(
            (ev: React.MouseEvent<HTMLButtonElement>) => {
                const newValue = (ev.target as HTMLButtonElement).value as
                    | KnownBaleChamberType
                    | KnownMachineHeaderType;

                if (!canToggle && value === newValue) {
                    return;
                }

                onChange(value === newValue ? null : newValue);
            },
            [onChange, canToggle, value],
        );

        if (options.length === 0) {
            return null;
        }

        return (
            <RadioGroup value={value ?? ''}>
                {options.map(option => (
                    <FormControlLabel
                        key={option.id}
                        value={option.id}
                        control={
                            <Radio onClick={handleRadioClick} size="small" />
                        }
                        label={option.label}
                    />
                ))}
            </RadioGroup>
        );
    },
);

RadioSelectFilter.displayName = 'RadioSelectFilter';
